import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Dialog } from '../TovDialog/TovDialog';

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'tov:flex tov:h-full tov:w-full tov:flex-col tov:overflow-hidden tov:rounded-md tov:bg-popover tov:text-popover-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog
      {...props}
      contentClassName={cn('tov:overflow-hidden tov:p-0', className)}
      description={description}
      headerClassName="tov:sr-only"
      showCloseButton={showCloseButton}
      title={title}
    >
      <Command className="tov:p-2 tov:**:data-[slot=command-input-wrapper]:h-14 tov:[&_[cmdk-input-wrapper]]:mx-1 tov:[&_[cmdk-input-wrapper]]:rounded-md tov:[&_[cmdk-input-wrapper]]:border tov:[&_[cmdk-input-wrapper]]:bg-background/80 tov:[&_[cmdk-group-heading]]:px-3 tov:[&_[cmdk-group-heading]]:pt-3 tov:[&_[cmdk-group-heading]]:pb-2 tov:[&_[cmdk-group-heading]]:font-medium tov:[&_[cmdk-group-heading]]:text-muted-foreground tov:[&_[cmdk-group]]:px-1 tov:[&_[cmdk-group]]:py-1 tov:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-2 tov:[&_[cmdk-input-wrapper]_svg]:h-5 tov:[&_[cmdk-input-wrapper]_svg]:w-5 tov:[&_[cmdk-input]]:h-14 tov:[&_[cmdk-item]]:px-3 tov:[&_[cmdk-item]]:py-3 tov:[&_[cmdk-item]_svg]:h-5 tov:[&_[cmdk-item]_svg]:w-5">
        {children}
      </Command>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="tov:flex tov:h-9 tov:items-center tov:gap-2 tov:border-b tov:px-3"
    >
      <SearchIcon className="tov:size-4 tov:shrink-0 tov:opacity-50" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'tov:flex tov:h-10 tov:w-full tov:rounded-md tov:bg-transparent tov:py-3 tov:text-sm tov:outline-hidden tov:placeholder:text-muted-foreground tov:disabled:cursor-not-allowed tov:disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        'tov:max-h-[min(24rem,calc(100dvh-12rem))] tov:scroll-py-3 tov:overflow-x-hidden tov:overflow-y-auto tov:pt-2',
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="tov:py-6 tov:text-center tov:text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'tov:overflow-hidden tov:p-1 tov:text-foreground tov:[&_[cmdk-group-heading]]:px-2 tov:[&_[cmdk-group-heading]]:py-1.5 tov:[&_[cmdk-group-heading]]:text-xs tov:[&_[cmdk-group-heading]]:font-medium tov:[&_[cmdk-group-heading]]:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('tov:-mx-1 tov:h-px tov:bg-border', className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'tov:relative tov:flex tov:cursor-default tov:items-center tov:gap-2 tov:rounded-sm tov:px-2 tov:py-1.5 tov:text-sm tov:outline-hidden tov:select-none tov:data-[disabled=true]:pointer-events-none tov:data-[disabled=true]:opacity-50 tov:data-[selected=true]:bg-accent tov:data-[selected=true]:text-accent-foreground tov:[&_svg]:pointer-events-none tov:[&_svg]:shrink-0 tov:[&_svg:not([class*=size-])]:size-4 tov:[&_svg:not([class*=text-])]:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'tov:ms-auto tov:text-xs tov:tracking-widest tov:text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
